import type { ReactNode } from 'react';
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { withMask } from 'use-mask-input';
import { Field } from '@/components/ui/admin/field';
import { cn } from '@/lib/utils';
import { type ColSpan, spanToClassName } from './form-input';

type FormMaskedInputProps<T extends FieldValues> = {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;
  placeholder?: string;
  type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
  autoComplete?: string;

  sm?: ColSpan;
  md?: ColSpan;
  lg?: ColSpan;

  className?: string;

  leftAddon?: ReactNode;
  rightAddon?: ReactNode;
  mask: string | string[];
} & React.ComponentProps<'input'>;

export function FormMaskedInput<T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  type = 'text',
  autoComplete,
  leftAddon,
  rightAddon,
  sm,
  md,
  lg,
  mask,
  className,
  required,
  ...props
}: FormMaskedInputProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <div className={cn(spanToClassName(sm, md, lg), className)}>
          <Field
            {...field}
            {...props}
            id={name}
            label={label}
            required={required}
            invalid={fieldState.invalid}
            value={field.value ?? ''}
            placeholder={placeholder}
            type={type ?? 'text'}
            autoComplete={autoComplete}
            aria-invalid={fieldState.invalid}
            leading={leftAddon}
            trailing={rightAddon}
            ref={withMask(mask, { removeMaskOnSubmit: true })}
          />

          {fieldState.error && (
            <p className="mt-1.5 text-xs font-medium text-destructive">
              {fieldState.error.message}
            </p>
          )}
        </div>
      )}
    />
  );
}
